import React, { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, EllipsisHorizontalIcon, ClockIcon } from '@heroicons/react/20/solid';

type CalendarView = 'month' | 'week' | 'day' | 'year';

interface CalendarEvent {
  id: string;
  name: string;
  datetime: string;
  time?: string;
  location?: string;
  color?: 'blue' | 'pink' | 'indigo' | 'gray';
}

interface CalendarProps {
  events?: CalendarEvent[];
  defaultView?: CalendarView;
  onAddEvent?: () => void;
  onEventClick?: (event: CalendarEvent) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  events = [],
  defaultView = 'month',
  onAddEvent,
  onEventClick,
}) => {
  const [currentView, setCurrentView] = useState<CalendarView>(defaultView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Date manipulation functions
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatDayFull = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDayName = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  // Generate calendar days for month view
  const generateMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Array<{ date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }> = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
      });
    }

    // Current month days
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: selectedDate ? date.toDateString() === selectedDate.toDateString() : false,
      });
    }

    return days;
  };

  // Generate week days
  const generateWeekDays = () => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek + 1); // Start on Monday

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }

    return days;
  };

  // Generate hours for day/week view
  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i === 0 ? 12 : i > 12 ? i - 12 : i;
    const period = i < 12 ? 'AM' : 'PM';
    return `${hour}${period}`;
  });

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.datetime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Color mapping for events
  const getColorClasses = (color: string = 'gray') => {
    const colors = {
      blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50',
      pink: 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/50',
      indigo: 'bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/70',
      gray: 'bg-gray-100 dark:bg-gray-800 text-label hover:bg-gray-200 dark:hover:bg-gray-700',
    };
    return colors[color as keyof typeof colors] || colors.gray;
  };

  // Render Month View
  const renderMonthView = () => {
    const days = generateMonthDays();

    return (
      <div className="shadow-sm ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 dark:border-gray-700 bg-gray-200 dark:bg-gray-700 text-center text-xs font-semibold text-label lg:flex-none">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="flex justify-center bg-panel py-2">
              <span>{day.charAt(0)}</span>
              <span className="sr-only sm:not-sr-only">{day.slice(1)}</span>
            </div>
          ))}
        </div>
        <div className="flex bg-gray-200 dark:bg-gray-700 text-xs text-label lg:flex-auto">
          <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
            {days.map((day, idx) => {
              const dayEvents = getEventsForDate(day.date);
              return (
                <div
                  key={idx}
                  className={`group relative px-3 py-2 ${
                    day.isCurrentMonth ? 'bg-panel' : 'bg-gray-50 dark:bg-gray-900 text-muted'
                  }`}
                  data-is-current-month={day.isCurrentMonth || undefined}
                  data-is-today={day.isToday || undefined}
                >
                  <time
                    dateTime={day.date.toISOString()}
                    className={`relative ${
                      !day.isCurrentMonth ? 'opacity-75' : ''
                    } ${
                      day.isToday
                        ? 'flex size-6 items-center justify-center rounded-full bg-slate-700 dark:bg-slate-800 font-semibold text-white'
                        : ''
                    }`}
                  >
                    {day.date.getDate()}
                  </time>
                  {dayEvents.length > 0 && (
                    <ol className="mt-2">
                      {dayEvents.map(event => (
                        <li key={event.id}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onEventClick?.(event);
                            }}
                            className="group flex"
                          >
                            <p className="flex-auto truncate font-medium text-body group-hover:text-slate-700 dark:group-hover:text-slate-400">
                              {event.name}
                            </p>
                            {event.time && (
                              <time className="ml-3 hidden flex-none text-muted group-hover:text-slate-700 dark:group-hover:text-slate-400 xl:block">
                                {event.time}
                              </time>
                            )}
                          </a>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>
          {/* Mobile Month View */}
          <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
            {days.map((day, idx) => {
              const dayEvents = getEventsForDate(day.date);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedDate(day.date)}
                  className={`group relative flex h-14 flex-col px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 focus:z-10 ${
                    !day.isCurrentMonth ? 'bg-gray-50 dark:bg-gray-900' : 'bg-panel'
                  } ${day.isSelected ? 'font-semibold text-white' : ''} ${
                    day.isToday && !day.isSelected ? 'font-semibold text-slate-700 dark:text-slate-400' : ''
                  }`}
                  data-is-current-month={day.isCurrentMonth || undefined}
                  data-is-selected={day.isSelected || undefined}
                  data-is-today={day.isToday || undefined}
                >
                  <time
                    dateTime={day.date.toISOString()}
                    className={`ml-auto ${
                      !day.isCurrentMonth ? 'opacity-75' : ''
                    } ${
                      day.isSelected
                        ? `flex size-6 items-center justify-center rounded-full ${
                            day.isToday ? 'bg-slate-700 dark:bg-slate-800' : 'bg-slate-700 dark:bg-slate-800'
                          }`
                        : ''
                    }`}
                  >
                    {day.date.getDate()}
                  </time>
                  {dayEvents.length > 0 && (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                      {dayEvents.slice(0, 3).map((_, i) => (
                        <span key={i} className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400" />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDays = generateWeekDays();
    const today = new Date();

    return (
      <div className="isolate flex flex-auto flex-col overflow-auto bg-white dark:bg-gray-900">
        <div style={{ width: '165%' }} className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
          <div className="sticky top-0 z-30 flex-none bg-white dark:bg-gray-900 shadow-sm ring-1 ring-black/5 sm:pr-8">
            <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 dark:divide-gray-700 border-r border-gray-100 dark:border-gray-700 text-sm text-muted sm:grid">
              <div className="col-end-1 w-14"></div>
              {weekDays.map((day, idx) => {
                const isToday = day.toDateString() === today.toDateString();
                return (
                  <div key={idx} className="flex items-center justify-center py-3">
                    <span className={isToday ? 'flex items-baseline' : ''}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}{' '}
                      <span
                        className={`ml-1.5 items-center justify-center font-semibold ${
                          isToday
                            ? 'flex size-8 rounded-full bg-slate-700 dark:bg-slate-800 text-white'
                            : 'text-body'
                        }`}
                      >
                        {day.getDate()}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-auto">
            <div className="sticky left-0 z-10 w-14 flex-none bg-white dark:bg-gray-900 ring-1 ring-gray-100 dark:ring-gray-700"></div>
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100 dark:divide-gray-700"
              >
                <div className="row-end-1 h-7"></div>
                {hours.map((hour, idx) => (
                  <React.Fragment key={idx}>
                    <div>
                      <div className="sticky left-0 z-20 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs text-gray-400 dark:text-gray-500">
                        {hour}
                      </div>
                    </div>
                    <div></div>
                  </React.Fragment>
                ))}
              </div>

              {/* Vertical lines */}
              <div className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 dark:divide-gray-700 sm:grid sm:grid-cols-7">
                {weekDays.map((_, idx) => (
                  <div key={idx} className={`col-start-${idx + 1} row-span-full`}></div>
                ))}
                <div className="col-start-8 row-span-full w-8"></div>
              </div>

              {/* Events */}
              <ol
                style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0, 1fr)) auto' }}
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
              >
                {/* Event rendering would go here */}
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Day View
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="isolate flex flex-auto overflow-hidden bg-white dark:bg-gray-900">
        <div className="flex flex-auto flex-col overflow-auto">
          <div className="flex w-full flex-auto">
            <div className="w-14 flex-none bg-white dark:bg-gray-900 ring-1 ring-gray-100 dark:ring-gray-700"></div>
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100 dark:divide-gray-700"
              >
                <div className="row-end-1 h-7"></div>
                {hours.map((hour, idx) => (
                  <React.Fragment key={idx}>
                    <div>
                      <div className="-mt-2.5 -ml-14 w-14 pr-2 text-right text-xs text-gray-400 dark:text-gray-500">
                        {hour}
                      </div>
                    </div>
                    <div></div>
                  </React.Fragment>
                ))}
              </div>

              {/* Events */}
              <ol
                style={{ gridTemplateRows: '1.75rem repeat(288, minmax(0, 1fr)) auto' }}
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
              >
                {dayEvents.map((event, idx) => (
                  <li
                    key={event.id}
                    style={{ gridRow: `${74 + idx * 20} / span 12` }}
                    className="relative mt-px flex"
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onEventClick?.(event);
                      }}
                      className={`group absolute inset-1 flex flex-col overflow-y-auto rounded-lg p-2 text-xs ${getColorClasses(
                        event.color
                      )}`}
                    >
                      <p className="order-1 font-semibold">{event.name}</p>
                      {event.location && <p className="order-1">{event.location}</p>}
                      {event.time && <p><time dateTime={event.datetime}>{event.time}</time></p>}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Year View
  const renderYearView = () => {
    const year = currentDate.getFullYear();
    const months = Array.from({ length: 12 }, (_, i) => {
      const monthDate = new Date(year, i, 1);
      return {
        name: monthDate.toLocaleDateString('en-US', { month: 'long' }),
        firstDay: monthDate.getDay(),
        daysInMonth: new Date(year, i + 1, 0).getDate(),
        monthIndex: i,
      };
    });

    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="mx-auto grid max-w-3xl grid-cols-1 gap-x-8 gap-y-16 px-4 py-16 sm:grid-cols-2 sm:px-6 xl:max-w-none xl:grid-cols-3 xl:px-8 2xl:grid-cols-4">
          {months.map((month) => {
            const days = [];
            // Add empty cells for days before month starts
            for (let i = 0; i < month.firstDay; i++) {
              days.push(null);
            }
            // Add days of the month
            for (let day = 1; day <= month.daysInMonth; day++) {
              days.push(day);
            }

            return (
              <section key={month.name} className="text-center">
                <h2 className="text-sm font-semibold text-heading">{month.name}</h2>
                <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-muted">
                  <div>M</div>
                  <div>T</div>
                  <div>W</div>
                  <div>T</div>
                  <div>F</div>
                  <div>S</div>
                  <div>S</div>
                </div>
                <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-700 text-sm shadow ring-1 ring-gray-200 dark:ring-gray-700">
                  {days.map((day, idx) => {
                    if (day === null) {
                      return <div key={`empty-${idx}`} className="bg-gray-50 dark:bg-gray-900 py-1.5" />;
                    }
                    const date = new Date(year, month.monthIndex, day);
                    const isToday = date.toDateString() === new Date().toDateString();
                    const hasEvents = getEventsForDate(date).length > 0;

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          setCurrentDate(date);
                          setCurrentView('day');
                        }}
                        className={`bg-white dark:bg-gray-800 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 focus:z-10 ${
                          idx === 0 ? 'rounded-tl-lg' : ''
                        } ${idx === 6 ? 'rounded-tr-lg' : ''} ${
                          idx === days.length - 7 ? 'rounded-bl-lg' : ''
                        } ${idx === days.length - 1 ? 'rounded-br-lg' : ''}`}
                      >
                        <time
                          dateTime={date.toISOString()}
                          className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full ${
                            isToday ? 'bg-slate-700 dark:bg-slate-800 font-semibold text-white' : ''
                          } ${hasEvents && !isToday ? 'font-semibold text-slate-700 dark:text-slate-400' : ''}`}
                        >
                          {day}
                        </time>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="lg:flex lg:h-full lg:flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4 lg:flex-none">
        <h1 className="text-base font-semibold text-heading">
          {currentView === 'day' ? (
            <>
              <time dateTime={currentDate.toISOString()} className="sm:hidden">
                {formatDayFull(currentDate)}
              </time>
              <time dateTime={currentDate.toISOString()} className="hidden sm:inline">
                {formatDayFull(currentDate)}
              </time>
              <p className="mt-1 text-sm text-muted">{getDayName(currentDate)}</p>
            </>
          ) : currentView === 'year' ? (
            <time dateTime={currentDate.getFullYear().toString()}>{currentDate.getFullYear()}</time>
          ) : (
            <time dateTime={currentDate.toISOString()}>{formatMonthYear(currentDate)}</time>
          )}
        </h1>
        <div className="flex items-center">
          {/* Navigation */}
          <div className="relative flex items-center rounded-md bg-panel shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 md:items-stretch">
            <button
              type="button"
              onClick={navigatePrevious}
              className="flex h-9 w-12 items-center justify-center rounded-l-md pr-1 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50 md:dark:hover:bg-gray-700"
            >
              <span className="sr-only">Previous {currentView}</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={goToToday}
              className="hidden px-3.5 text-sm font-semibold text-body hover:bg-highlight focus:relative md:block"
            >
              Today
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 dark:bg-gray-600 md:hidden"></span>
            <button
              type="button"
              onClick={navigateNext}
              className="flex h-9 w-12 items-center justify-center rounded-r-md pl-1 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50 md:dark:hover:bg-gray-700"
            >
              <span className="sr-only">Next {currentView}</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* View Switcher & Add Event Button - Desktop */}
          <div className="hidden md:ml-4 md:flex md:items-center">
            <div className="relative">
              <select
                value={currentView}
                onChange={(e) => setCurrentView(e.target.value as CalendarView)}
                className="block appearance-none rounded-md bg-panel px-3 py-2 pr-8 text-sm font-semibold text-body shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight focus:outline-none focus:ring-2 focus:ring-slate-600"
              >
                <option value="day">Day view</option>
                <option value="week">Week view</option>
                <option value="month">Month view</option>
                <option value="year">Year view</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronRightIcon className="h-5 w-5 rotate-90 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              </div>
            </div>
            <div className="ml-6 h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
            <button
              type="button"
              onClick={onAddEvent}
              className="ml-6 rounded-md bg-slate-700 dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-600 dark:hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600"
            >
              Add event
            </button>
          </div>

          {/* Mobile Menu */}
          <div className="relative ml-6 md:hidden">
            <button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400">
              <span className="sr-only">Open menu</span>
              <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Calendar Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {currentView === 'month' && renderMonthView()}
        {currentView === 'week' && renderWeekView()}
        {currentView === 'day' && renderDayView()}
        {currentView === 'year' && renderYearView()}
      </div>

      {/* Mobile Event List (for month view) */}
      {currentView === 'month' && (
        <div className="px-4 py-10 sm:px-6 lg:hidden">
          <ol className="divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden rounded-lg bg-panel text-sm shadow-sm ring-1 ring-black/5 dark:ring-gray-700">
            {selectedDate && getEventsForDate(selectedDate).map((event) => (
              <li key={event.id} className="group flex p-4 pr-6 hover:bg-highlight">
                <div className="flex-auto">
                  <p className="font-semibold text-heading">{event.name}</p>
                  {event.time && (
                    <time dateTime={event.datetime} className="mt-2 flex items-center text-label">
                      <ClockIcon className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      {event.time}
                    </time>
                  )}
                </div>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onEventClick?.(event);
                  }}
                  className="ml-6 flex-none self-center rounded-md bg-panel px-3 py-2 font-semibold text-body opacity-0 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:ring-gray-400 dark:hover:ring-gray-500 group-hover:opacity-100 focus:opacity-100"
                >
                  Edit<span className="sr-only">, {event.name}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default Calendar;
